import * as React from 'react'
import {
  addDays,
  differenceInCalendarDays,
  format,
  isSameMonth,
  isSameYear,
  isToday,
  startOfDay,
  startOfWeek,
} from 'date-fns'
import {
  ChevronLeft,
  ChevronRight,
  LoaderCircle,
  TriangleAlert,
} from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { APPOINTMENTS_DAY_INITIAL_VISIBLE } from '@/config/constants'
import { cn } from '@/lib/utils'
import type {
  AppointmentRecord,
  AvailabilityBlock,
} from '@/features/appointments/types'
import { parseAppointmentDate } from '@/features/appointments/utils/parseAppointmentDate'
import {
  getAppointmentDate,
  getAppointmentSortKey,
} from '../utils/calendarAppointments'
import { AppointmentsCalendarCard } from './AppointmentsCalendarCard'
import { OpenSlotCard } from './OpenSlotCard'

/** One day-column entry: either a booked appointment or an open-slot block. */
type DayEntry =
  | { kind: 'appointment'; appointment: AppointmentRecord; key: number }
  | { kind: 'openSlot'; block: AvailabilityBlock; key: number }

/** The calendar's two range modes: a 3-day window or a Sun–Sat week. */
export type CalendarRangeDays = 3 | 7

// Stable empty default so `openSlots` omitted by a caller doesn't create a
// fresh array identity every render (would otherwise churn the memo below).
const EMPTY_OPEN_SLOTS: AvailabilityBlock[] = []

/**
 * Default range start for "today": the 3-day window centers today
 * (yesterday | today | tomorrow); the week anchors to Sunday.
 */
export function startOfCurrentRange(rangeDays: CalendarRangeDays): Date {
  return rangeDays === 7
    ? startOfWeek(new Date(), { weekStartsOn: 0 })
    : startOfDay(addDays(new Date(), -1))
}

/** Which calendar day an open-slot block belongs to — keyed off block_start,
 * the same field it's sorted/displayed by (unlike appointments, there's no
 * separate zone-free date column to prefer). */
function getBlockDate(block: AvailabilityBlock): Date | null {
  return parseAppointmentDate(block.block_start)
}

function getBlockSortKey(block: AvailabilityBlock): number {
  const start = parseAppointmentDate(block.block_start)
  return start ? start.getTime() : Number.MAX_SAFE_INTEGER
}

/**
 * Calendar view for appointments: one column per day (3-day window by
 * default, full week on demand), each appointment rendered as a card showing
 * the same at-a-glance fields as the Appointments table (time, patient,
 * type, provider, status). Day columns scroll independently, and very long
 * days render a capped list with a "Show N more" affordance.
 *
 * Purely presentational: pass appointments already filtered — the page owns
 * filter state so the same filters (provider, status, …) drive both the
 * table and calendar views. The visible range is controlled (`rangeStart` +
 * `rangeDays`) so the page can key its range-bounded query off it.
 */
export function AppointmentsCalendar({
  appointments,
  openSlots = EMPTY_OPEN_SLOTS,
  rangeStart,
  rangeDays,
  onRangeStartChange,
  onRangeDaysChange,
  onAppointmentSelect,
  selectedAppointmentId,
  isLoading = false,
  isFetching = false,
  isError = false,
  error = null,
  onRetry,
  truncated = false,
}: {
  selectedAppointmentId?: string
  appointments: AppointmentRecord[]
  onAppointmentSelect?: (appointment: AppointmentRecord) => void
  /** Coalesced open-availability blocks, interleaved chronologically
   * alongside appointments within each day column. The page passes none
   * when the Open Slots filter hides them. */
  openSlots?: AvailabilityBlock[]
  /** First visible day (see `startOfCurrentRange`). */
  rangeStart: Date
  rangeDays: CalendarRangeDays
  onRangeStartChange: (rangeStart: Date) => void
  onRangeDaysChange: (rangeDays: CalendarRangeDays) => void
  /** No data for this range yet: replaces the grid with the loading state. */
  isLoading?: boolean
  /** Refetch of the already-shown range: keeps the grid visible but dimmed. */
  isFetching?: boolean
  /** Failed range: replaces the grid with an error state, never an empty one. */
  isError?: boolean
  error?: Error | null
  onRetry?: () => void
  /** The range query hit its page cap — the tail of the range may be missing. */
  truncated?: boolean
}) {
  // Days a long column has been expanded for, keyed by yyyy-MM-dd.
  const [expandedDays, setExpandedDays] = React.useState<Set<string>>(
    () => new Set()
  )

  const days = React.useMemo(
    () => Array.from({ length: rangeDays }, (_, i) => addDays(rangeStart, i)),
    [rangeStart, rangeDays]
  )

  // One pass over both sources: parse each row's day once, bucket by day
  // index, and precompute the sort key so the comparator never re-parses.
  // Rows outside the visible range (the queries over-fetch a day each side)
  // are dropped, not clamped into an edge column. Appointments and open
  // slots share one chronological list per day — one timeline of the day,
  // not two separate sections.
  const entriesByDay = React.useMemo(() => {
    // Backend availability already accounts for booked capacity. Open blocks
    // can overlap appointment cards when capacity remains or a booking is cancelled.
    const buckets: DayEntry[][] = Array.from({ length: rangeDays }, () => [])
    for (const appointment of appointments) {
      const date = getAppointmentDate(appointment)
      if (!date) continue
      const index = differenceInCalendarDays(date, rangeStart)
      if (index >= 0 && index < rangeDays) {
        buckets[index]!.push({
          kind: 'appointment',
          appointment,
          key: getAppointmentSortKey(appointment),
        })
      }
    }
    for (const block of openSlots) {
      const date = getBlockDate(block)
      if (!date) continue
      const index = differenceInCalendarDays(date, rangeStart)
      if (index >= 0 && index < rangeDays) {
        buckets[index]!.push({
          kind: 'openSlot',
          block,
          key: getBlockSortKey(block),
        })
      }
    }
    return buckets.map((bucket) => bucket.toSorted((a, b) => a.key - b.key))
  }, [appointments, openSlots, rangeStart, rangeDays])

  // Keep a selected card rendered after closing the sheet so focus can return
  // to it, including when navigation reaches a collapsed part of a long day.
  React.useEffect(() => {
    const selected = appointments.find(
      (row) => row.id === selectedAppointmentId
    )
    const date = selected && getAppointmentDate(selected)
    if (!date) return
    const key = format(date, 'yyyy-MM-dd')
    setExpandedDays((previous) =>
      previous.has(key) ? previous : new Set([...previous, key])
    )
  }, [appointments, selectedAppointmentId])

  const rangeEnd = days[days.length - 1] as Date
  const sameYear = isSameYear(rangeStart, rangeEnd)
  const rangeLabel = `${format(
    rangeStart,
    sameYear ? 'MMM d' : 'MMM d, yyyy'
  )} – ${format(
    rangeEnd,
    sameYear && isSameMonth(rangeStart, rangeEnd) ? 'd, yyyy' : 'MMM d, yyyy'
  )}`

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 pb-3">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={`Previous ${rangeDays} days`}
            onClick={() => onRangeStartChange(addDays(rangeStart, -rangeDays))}>
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={`Next ${rangeDays} days`}
            onClick={() => onRangeStartChange(addDays(rangeStart, rangeDays))}>
            <ChevronRight />
          </Button>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRangeStartChange(startOfCurrentRange(rangeDays))}>
          Today
        </Button>
        <span aria-live="polite" className="text-sm font-semibold">
          {rangeLabel}
        </span>
        <div className="ml-auto">
          <ToggleGroup
            type="single"
            value={String(rangeDays)}
            onValueChange={(next) => {
              if (next === '3') onRangeDaysChange(3)
              if (next === '7') onRangeDaysChange(7)
            }}
            variant="outline"
            size="sm">
            <ToggleGroupItem value="3" aria-label="3-day view">
              3 days
            </ToggleGroupItem>
            <ToggleGroupItem value="7" aria-label="Week view">
              Week
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      {truncated && !isLoading && !isError ? (
        <Alert className="mb-3">
          <TriangleAlert className="h-4 w-4" />
          <AlertTitle>These days are only partially loaded</AlertTitle>
          <AlertDescription>
            There is more data than the calendar could fetch — the end of the
            range may be missing appointments or open slots. Narrow the filters
            to see everything.
          </AlertDescription>
        </Alert>
      ) : null}

      {isError ? (
        <div className="flex min-h-32 flex-1 items-center justify-center rounded-lg border bg-background p-6">
          <div className="flex max-w-md flex-col items-center gap-3 text-center">
            <Alert variant="destructive" className="text-left">
              <TriangleAlert className="h-4 w-4" />
              <AlertTitle>Couldn&apos;t load appointments</AlertTitle>
              <AlertDescription>
                {error instanceof Error && error.message
                  ? error.message
                  : 'Something went wrong on our end. Please try again.'}
              </AlertDescription>
            </Alert>
            {onRetry ? (
              <Button size="sm" onClick={onRetry}>
                Retry
              </Button>
            ) : null}
          </div>
        </div>
      ) : isLoading ? (
        <div className="flex min-h-32 flex-1 items-center justify-center gap-2 rounded-lg border bg-background">
          <LoaderCircle className="text-muted-foreground h-4 w-4 animate-spin-gpu" />
          <span className="text-muted-foreground text-sm">
            Loading appointments…
          </span>
        </div>
      ) : (
        <div
          aria-busy={isFetching}
          className={cn(
            'grid min-h-0 flex-1 divide-x overflow-hidden rounded-lg border bg-background transition-opacity',
            rangeDays === 7 ? 'grid-cols-7' : 'grid-cols-3',
            isFetching && 'pointer-events-none opacity-50'
          )}>
          {days.map((day, index) => {
            const dayEntries = entriesByDay[index] ?? []
            const today = isToday(day)
            const dayKey = format(day, 'yyyy-MM-dd')
            const expanded =
              expandedDays.has(dayKey) ||
              dayEntries.some(
                (entry) =>
                  entry.kind === 'appointment' &&
                  entry.appointment.id === selectedAppointmentId
              )
            // Fair-share the cap between appointments and open slots rather
            // than letting either one starve the other: a busy provider
            // roster can post far more "Open" blocks in a day than there are
            // bookings (or vice versa on a slow day), so neither a pure
            // chronological cap NOR an appointments-always-win cap works —
            // either can reduce a day to 100% one kind and 0% the other.
            // Each kind gets half the cap; a kind that doesn't need its full
            // half lets the other reclaim the leftover. The chosen set is
            // re-sorted chronologically so the rendered order is unaffected.
            let visibleEntries = dayEntries
            if (
              !expanded &&
              dayEntries.length > APPOINTMENTS_DAY_INITIAL_VISIBLE
            ) {
              const appointmentEntries = dayEntries.filter(
                (entry) => entry.kind === 'appointment'
              )
              const openSlotEntries = dayEntries.filter(
                (entry) => entry.kind === 'openSlot'
              )
              const half = Math.ceil(APPOINTMENTS_DAY_INITIAL_VISIBLE / 2)
              const appointmentBudget = Math.min(
                appointmentEntries.length,
                half
              )
              const openSlotBudget = Math.min(
                openSlotEntries.length,
                APPOINTMENTS_DAY_INITIAL_VISIBLE - appointmentBudget
              )
              // Open slots left their share unused (few of them today) —
              // let appointments reclaim it, and vice versa is impossible
              // here since appointmentBudget already took its full half
              // whenever there were enough appointments to fill it.
              const reclaimedAppointmentBudget = Math.min(
                appointmentEntries.length,
                APPOINTMENTS_DAY_INITIAL_VISIBLE - openSlotBudget
              )
              const visibleAppointments = appointmentEntries.slice(
                0,
                reclaimedAppointmentBudget
              )
              const visibleOpenSlots = openSlotEntries.slice(0, openSlotBudget)
              visibleEntries = [
                ...visibleAppointments,
                ...visibleOpenSlots,
              ].toSorted((a, b) => a.key - b.key)
            }
            const hiddenCount = dayEntries.length - visibleEntries.length
            return (
              <div
                key={dayKey}
                role="group"
                aria-label={format(day, 'EEEE, MMMM d')}
                className="flex min-h-0 min-w-0 flex-col">
                <div
                  className={cn(
                    'border-b px-2 py-1.5 text-center',
                    today ? 'bg-primary-ui-bg' : 'bg-muted/50'
                  )}>
                  <time dateTime={dayKey}>
                    <div className="text-xs font-medium text-muted-foreground uppercase">
                      {format(day, 'EEE')}
                    </div>
                    <div
                      className={cn(
                        'text-sm font-semibold tabular-nums',
                        today && 'text-primary-solid'
                      )}>
                      {format(day, 'd')}
                    </div>
                  </time>
                </div>
                <div
                  role="list"
                  className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto p-1.5">
                  {dayEntries.length === 0 ? (
                    <div className="py-6 text-center text-xs text-muted-foreground/60">
                      Nothing scheduled
                    </div>
                  ) : (
                    <>
                      {visibleEntries.map((entry) =>
                        entry.kind === 'appointment' ? (
                          <AppointmentsCalendarCard
                            key={entry.appointment.id}
                            appointment={entry.appointment}
                            onSelect={onAppointmentSelect}
                          />
                        ) : (
                          <OpenSlotCard
                            key={
                              entry.block.appointment_interval_id ??
                              `${entry.block.provider_id}-${entry.block.block_start}`
                            }
                            block={entry.block}
                          />
                        )
                      )}
                      {hiddenCount > 0 ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="shrink-0"
                          onClick={() =>
                            setExpandedDays((prev) => new Set(prev).add(dayKey))
                          }>
                          Show {hiddenCount} more
                        </Button>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
